import React, { useEffect, useState, useRef } from 'react';
import { DollarSignIcon, PlusIcon, SearchIcon, EditIcon, TrashIcon, SettingsIcon, ShoppingCartIcon, CalendarIcon, UserIcon, StethoscopeIcon, PackageIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Tabs } from '../ui/Tabs';
import { FormSection } from '../ui/FormSection';
import { Pagination } from '../ui/Pagination';
import { SortableHeader } from '../ui/SortableHeader';
import { formatCurrency, getToday } from '../../utils/helpers';
import { usePagination } from '../../hooks/usePagination';
import { useSortableData } from '../../hooks/useSortableData';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { supabase } from '../../utils/supabase';
interface CommissionConfig {
  id?: string;
  person_id: string;
  person_name: string;
  person_type: 'usuario' | 'doctor';
  commission_percent: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}
interface ServiceCommission {
  date: string;
  patient_name: string;
  service_name: string;
  price: number;
  doctor_name: string;
  doctor_commission: number;
  user_name: string;
  user_commission: number;
}
interface ProductCommission {
  date: string;
  product_name: string;
  quantity: number;
  total: number;
  seller_name: string;
  commission_amount: number;
}
export function Commissions() {
  const {
    patients,
    doctors,
    appointments,
    services,
    getPatient,
    getDoctor,
    getService
  } = useData();
  const {
    user
  } = useAuth();
  const {
    addNotification
  } = useNotifications();
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState<CommissionConfig[]>([]);
  const [editingConfig, setEditingConfig] = useState<CommissionConfig | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchConfig, setSearchConfig] = useState('');
  const [userHasEdited, setUserHasEdited] = useState(false);
  const configRef = useRef<CommissionConfig[]>([]);
  const saveTimeoutRef = useRef<NodeJS.Timeout>();
  // Service commissions filters
  const [serviceStartDate, setServiceStartDate] = useState('');
  const [serviceEndDate, setServiceEndDate] = useState('');
  const [serviceDoctor, setServiceDoctor] = useState('');
  const [serviceUser, setServiceUser] = useState('');
  const [searchService, setSearchService] = useState('');
  // Product commissions filters
  const [productStartDate, setProductStartDate] = useState('');
  const [productEndDate, setProductEndDate] = useState('');
  const [productSeller, setProductSeller] = useState('');
  const [searchProduct, setSearchProduct] = useState('');
  // Load commission configs
  useEffect(() => {
    loadConfigs();
  }, []);
  const loadConfigs = async () => {
    setLoading(true);
    try {
      // Try Supabase first
      const {
        data,
        error
      } = await supabase.from('commission_configs').select('*').order('person_name');
      if (!error && data) {
        setConfigs(data);
        configRef.current = data;
        localStorage.setItem('savidna-commission-config', JSON.stringify(data));
      } else {
        // Fallback to localStorage
        const saved = localStorage.getItem('savidna-commission-config');
        if (saved) {
          const parsed = JSON.parse(saved);
          setConfigs(parsed);
          configRef.current = parsed;
        } else {
          // Initialize with default configs for all doctors and users
          const defaultConfigs: CommissionConfig[] = [];
          doctors.forEach(doc => {
            defaultConfigs.push({
              person_id: doc.id,
              person_name: doc.name,
              person_type: 'doctor',
              commission_percent: 10,
              is_active: true
            });
          });
          // Add current user if not already in doctors
          if (user && !doctors.find(d => d.id === user.id)) {
            defaultConfigs.push({
              person_id: user.id,
              person_name: user.name,
              person_type: 'usuario',
              commission_percent: 10,
              is_active: true
            });
          }
          setConfigs(defaultConfigs);
          configRef.current = defaultConfigs;
        }
      }
    } catch (err) {
      console.error('Error loading configs:', err);
      addNotification('Error al cargar configuración de comisiones', 'error');
    }
    setLoading(false);
  };
  // Auto-save with debounce
  useEffect(() => {
    if (!userHasEdited) return;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        // Save to localStorage
        localStorage.setItem('savidna-commission-config', JSON.stringify(configRef.current));
        // Save to Supabase
        for (const config of configRef.current) {
          const {
            id,
            created_at,
            updated_at,
            ...configData
          } = config;
          if (id) {
            await supabase.from('commission_configs').update(configData).eq('id', id);
          } else {
            await supabase.from('commission_configs').upsert(configData, {
              onConflict: 'person_id'
            });
          }
        }
        addNotification('Configuración guardada automáticamente', 'success');
        setUserHasEdited(false);
      } catch (err) {
        console.error('Error saving configs:', err);
      }
    }, 800);
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [configs, userHasEdited, addNotification]);
  const handleAddConfig = () => {
    setEditingConfig({
      person_id: '',
      person_name: '',
      person_type: 'usuario',
      commission_percent: 10,
      is_active: true
    });
    setIsModalOpen(true);
  };
  const handleEditConfig = (config: CommissionConfig) => {
    setEditingConfig({
      ...config
    });
    setIsModalOpen(true);
  };
  const handleSaveConfig = () => {
    if (!editingConfig) return;
    const updatedConfigs = editingConfig.id ? configs.map(c => c.id === editingConfig.id ? editingConfig : c) : [...configs, {
      ...editingConfig,
      id: `temp-${Date.now()}`
    }];
    setConfigs(updatedConfigs);
    configRef.current = updatedConfigs;
    setUserHasEdited(true);
    setIsModalOpen(false);
    setEditingConfig(null);
  };
  const handleDeleteConfig = (id: string) => {
    if (!confirm('¿Eliminar esta configuración?')) return;
    const updatedConfigs = configs.filter(c => c.id !== id);
    setConfigs(updatedConfigs);
    configRef.current = updatedConfigs;
    setUserHasEdited(true);
  };
  const handleToggleActive = (id: string) => {
    const updatedConfigs = configs.map(c => c.id === id ? {
      ...c,
      is_active: !c.is_active
    } : c);
    setConfigs(updatedConfigs);
    configRef.current = updatedConfigs;
    setUserHasEdited(true);
  };
  // Calculate service commissions
  const calculateServiceCommissions = (): ServiceCommission[] => {
    const completedAppointments = appointments.filter(apt => apt.status === 'completada');
    const commissions: ServiceCommission[] = [];
    completedAppointments.forEach(apt => {
      const patient = getPatient(apt.patientId);
      const doctor = getDoctor(apt.doctorId);
      const service = getService(apt.serviceId);
      if (!patient || !doctor || !service) return;
      // Check date filters
      if (serviceStartDate && apt.date < serviceStartDate) return;
      if (serviceEndDate && apt.date > serviceEndDate) return;
      if (serviceDoctor && apt.doctorId !== serviceDoctor) return;
      if (serviceUser && apt.createdByUserId !== serviceUser) return;
      const doctorConfig = configs.find(c => c.person_id === doctor.id && c.is_active);
      const userConfig = configs.find(c => c.person_id === apt.createdByUserId && c.is_active);
      const doctorCommission = doctorConfig ? service.price * doctorConfig.commission_percent / 100 : 0;
      const userCommission = userConfig ? service.price * userConfig.commission_percent / 100 : 0;
      commissions.push({
        date: apt.date,
        patient_name: patient.name,
        service_name: service.name,
        price: service.price,
        doctor_name: doctor.name,
        doctor_commission: doctorCommission,
        user_name: apt.createdByUserName || 'N/A',
        user_commission: userCommission
      });
    });
    return commissions;
  };
  // Calculate product commissions
  const calculateProductCommissions = (): ProductCommission[] => {
    try {
      const savedSales = localStorage.getItem('savidna-product-sales');
      if (!savedSales) return [];
      const sales = JSON.parse(savedSales);
      const commissions: ProductCommission[] = [];
      sales.forEach((sale: any) => {
        // Check date filters
        if (productStartDate && sale.date < productStartDate) return;
        if (productEndDate && sale.date > productEndDate) return;
        if (productSeller && sale.created_by_user_id !== productSeller) return;
        const sellerConfig = configs.find(c => c.person_id === sale.created_by_user_id && c.is_active);
        const commissionAmount = sellerConfig ? sale.total * sellerConfig.commission_percent / 100 : 0;
        sale.items?.forEach((item: any) => {
          commissions.push({
            date: sale.date,
            product_name: item.product_name || 'N/A',
            quantity: item.quantity || 0,
            total: sale.total,
            seller_name: sale.created_by_user_name || 'N/A',
            commission_amount: commissionAmount
          });
        });
      });
      return commissions;
    } catch (err) {
      console.error('Error calculating product commissions:', err);
      return [];
    }
  };
  const serviceCommissions = calculateServiceCommissions();
  const productCommissions = calculateProductCommissions();
  // Filter configs for display
  const filteredConfigs = configs.filter(c => c.person_name.toLowerCase().includes(searchConfig.toLowerCase()));
  const {
    sortedItems: sortedConfigs
  } = useSortableData(filteredConfigs);
  const {
    paginatedItems: paginatedConfigs,
    ...configPagination
  } = usePagination(sortedConfigs, 10);
  // Filter service commissions
  const filteredServiceCommissions = serviceCommissions.filter(c => c.patient_name.toLowerCase().includes(searchService.toLowerCase()) || c.service_name.toLowerCase().includes(searchService.toLowerCase()));
  const {
    sortedItems: sortedServiceCommissions
  } = useSortableData(filteredServiceCommissions);
  const {
    paginatedItems: paginatedServiceCommissions,
    ...servicePagination
  } = usePagination(sortedServiceCommissions, 10);
  // Filter product commissions
  const filteredProductCommissions = productCommissions.filter(c => c.product_name.toLowerCase().includes(searchProduct.toLowerCase()));
  const {
    sortedItems: sortedProductCommissions
  } = useSortableData(filteredProductCommissions);
  const {
    paginatedItems: paginatedProductCommissions,
    ...productPagination
  } = usePagination(sortedProductCommissions, 10);
  // Calculate totals
  const serviceTotalDoctorCommission = filteredServiceCommissions.reduce((sum, c) => sum + c.doctor_commission, 0);
  const serviceTotalUserCommission = filteredServiceCommissions.reduce((sum, c) => sum + c.user_commission, 0);
  const productTotalCommission = filteredProductCommissions.reduce((sum, c) => sum + c.commission_amount, 0);
  const tabs = [{
    id: 'config',
    label: 'Configuración',
    icon: <SettingsIcon className="w-4 h-4" data-id="element-1193" />,
    content: <div className="space-y-4" data-id="element-1194">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between" data-id="element-1195">
            <Input placeholder="Buscar persona..." value={searchConfig} onChange={e => setSearchConfig(e.target.value)} className="w-full sm:w-64" data-id="element-1196" />
            <Button onClick={handleAddConfig} className="w-full sm:w-auto" data-id="element-1197">
              <PlusIcon className="w-4 h-4 mr-2" data-id="element-1198" />
              Agregar Configuración
            </Button>
          </div>

          <Card padding="none" data-id="element-1199">
            <div className="overflow-x-auto" data-id="element-1200">
              <table className="w-full text-sm" data-id="element-1201">
                <thead data-id="element-1202">
                  <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-1203">
                    <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300" data-id="element-1204">
                      Nombre
                    </th>
                    <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300" data-id="element-1205">
                      Tipo
                    </th>
                    <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300" data-id="element-1206">
                      Comisión %
                    </th>
                    <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300" data-id="element-1207">
                      Estado
                    </th>
                    <th className="text-right p-4 font-semibold text-gray-700 dark:text-gray-300" data-id="element-1208">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-1209">
                  {paginatedConfigs.length === 0 ? <tr data-id="element-1210">
                      <td colSpan={5} className="text-center p-8 text-gray-500" data-id="element-1211">
                        No hay configuraciones. Agrega una para comenzar.
                      </td>
                    </tr> : paginatedConfigs.map(config => <tr key={config.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-1212">
                        <td className="p-4" data-id="element-1213">
                          <div className="flex items-center gap-2" data-id="element-1214">
                            {config.person_type === 'doctor' ? <StethoscopeIcon className="w-4 h-4 text-purple-500" data-id="element-1215" /> : <UserIcon className="w-4 h-4 text-indigo-500" data-id="element-1216" />}
                            <span className="font-medium" data-id="element-1217">
                              {config.person_name}
                            </span>
                          </div>
                        </td>
                        <td className="p-4" data-id="element-1218">
                          <Badge variant={config.person_type === 'doctor' ? 'primary' : 'info'} data-id="element-1219">
                            {config.person_type === 'doctor' ? 'Doctor' : 'Usuario'}
                          </Badge>
                        </td>
                        <td className="p-4 font-semibold text-purple-600 dark:text-purple-400" data-id="element-1220">
                          {config.commission_percent}%
                        </td>
                        <td className="p-4" data-id="element-1221">
                          <button onClick={() => handleToggleActive(config.id!)} className="text-sm" data-id="element-1222">
                            <Badge variant={config.is_active ? 'success' : 'default'} data-id="element-1223">
                              {config.is_active ? 'Activo' : 'Inactivo'}
                            </Badge>
                          </button>
                        </td>
                        <td className="p-4" data-id="element-1224">
                          <div className="flex items-center justify-end gap-2" data-id="element-1225">
                            <Button variant="ghost" size="sm" onClick={() => handleEditConfig(config)} data-id="element-1226">
                              <EditIcon className="w-4 h-4" data-id="element-1227" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteConfig(config.id!)} data-id="element-1228">
                              <TrashIcon className="w-4 h-4 text-red-500" data-id="element-1229" />
                            </Button>
                          </div>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>
          </Card>

          <Pagination {...configPagination} data-id="element-1230" />
        </div>
  }, {
    id: 'services',
    label: 'Comisiones por Servicios',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-1231" />,
    content: <div className="space-y-4" data-id="element-1232">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1233">
            <Input type="date" label="Fecha Inicio" value={serviceStartDate} onChange={e => setServiceStartDate(e.target.value)} data-id="element-1234" />
            <Input type="date" label="Fecha Fin" value={serviceEndDate} onChange={e => setServiceEndDate(e.target.value)} data-id="element-1235" />
            <Select label="Doctor" options={[{
          value: '',
          label: 'Todos'
        }, ...doctors.map(d => ({
          value: d.id,
          label: d.name
        }))]} value={serviceDoctor} onChange={setServiceDoctor} data-id="element-1236" />
            <Input placeholder="Buscar..." value={searchService} onChange={e => setSearchService(e.target.value)} data-id="element-1237" />
          </div>

          <Card padding="none" data-id="element-1238">
            <div className="overflow-x-auto" data-id="element-1239">
              <table className="w-full text-sm" data-id="element-1240">
                <thead data-id="element-1241">
                  <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-1242">
                    <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300" data-id="element-1243">
                      Fecha
                    </th>
                    <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300" data-id="element-1244">
                      Paciente
                    </th>
                    <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300" data-id="element-1245">
                      Servicio
                    </th>
                    <th className="text-right p-4 font-semibold text-gray-700 dark:text-gray-300" data-id="element-1246">
                      Precio
                    </th>
                    <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300" data-id="element-1247">
                      Doctor
                    </th>
                    <th className="text-right p-4 font-semibold text-gray-700 dark:text-gray-300" data-id="element-1248">
                      Comisión Doctor
                    </th>
                    <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300" data-id="element-1249">
                      Usuario
                    </th>
                    <th className="text-right p-4 font-semibold text-gray-700 dark:text-gray-300" data-id="element-1250">
                      Comisión Usuario
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-1251">
                  {paginatedServiceCommissions.length === 0 ? <tr data-id="element-1252">
                      <td colSpan={8} className="text-center p-8 text-gray-500" data-id="element-1253">
                        No hay comisiones para mostrar
                      </td>
                    </tr> : paginatedServiceCommissions.map((comm, idx) => <tr key={idx} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-1254">
                        <td className="p-4" data-id="element-1255">{comm.date}</td>
                        <td className="p-4" data-id="element-1256">{comm.patient_name}</td>
                        <td className="p-4" data-id="element-1257">{comm.service_name}</td>
                        <td className="p-4 text-right" data-id="element-1258">
                          {formatCurrency(comm.price)}
                        </td>
                        <td className="p-4" data-id="element-1259">{comm.doctor_name}</td>
                        <td className="p-4 text-right font-semibold text-purple-600 dark:text-purple-400" data-id="element-1260">
                          {formatCurrency(comm.doctor_commission)}
                        </td>
                        <td className="p-4" data-id="element-1261">{comm.user_name}</td>
                        <td className="p-4 text-right font-semibold text-indigo-600 dark:text-indigo-400" data-id="element-1262">
                          {formatCurrency(comm.user_commission)}
                        </td>
                      </tr>)}
                </tbody>
                {filteredServiceCommissions.length > 0 && <tfoot className="bg-gray-50 dark:bg-gray-800 font-semibold" data-id="element-1263">
                    <tr data-id="element-1264">
                      <td colSpan={5} className="p-4 text-right" data-id="element-1265">
                        Totales:
                      </td>
                      <td className="p-4 text-right text-purple-600 dark:text-purple-400" data-id="element-1266">
                        {formatCurrency(serviceTotalDoctorCommission)}
                      </td>
                      <td className="p-4" data-id="element-1267"></td>
                      <td className="p-4 text-right text-indigo-600 dark:text-indigo-400" data-id="element-1268">
                        {formatCurrency(serviceTotalUserCommission)}
                      </td>
                    </tr>
                  </tfoot>}
              </table>
            </div>
          </Card>

          <Pagination {...servicePagination} data-id="element-1269" />
        </div>
  }, {
    id: 'products',
    label: 'Comisiones por Productos',
    icon: <PackageIcon className="w-4 h-4" data-id="element-1270" />,
    content: <div className="space-y-4" data-id="element-1271">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1272">
            <Input type="date" label="Fecha Inicio" value={productStartDate} onChange={e => setProductStartDate(e.target.value)} data-id="element-1273" />
            <Input type="date" label="Fecha Fin" value={productEndDate} onChange={e => setProductEndDate(e.target.value)} data-id="element-1274" />
            <Input placeholder="Buscar producto..." value={searchProduct} onChange={e => setSearchProduct(e.target.value)} data-id="element-1275" />
          </div>

          <Card padding="none" data-id="element-1276">
            <div className="overflow-x-auto" data-id="element-1277">
              <table className="w-full text-sm" data-id="element-1278">
                <thead data-id="element-1279">
                  <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-1280">
                    <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300" data-id="element-1281">
                      Fecha
                    </th>
                    <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300" data-id="element-1282">
                      Producto
                    </th>
                    <th className="text-right p-4 font-semibold text-gray-700 dark:text-gray-300" data-id="element-1283">
                      Cantidad
                    </th>
                    <th className="text-right p-4 font-semibold text-gray-700 dark:text-gray-300" data-id="element-1284">
                      Total
                    </th>
                    <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300" data-id="element-1285">
                      Vendedor
                    </th>
                    <th className="text-right p-4 font-semibold text-gray-700 dark:text-gray-300" data-id="element-1286">
                      Comisión
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-1287">
                  {paginatedProductCommissions.length === 0 ? <tr data-id="element-1288">
                      <td colSpan={6} className="text-center p-8 text-gray-500" data-id="element-1289">
                        No hay comisiones de productos para mostrar
                      </td>
                    </tr> : paginatedProductCommissions.map((comm, idx) => <tr key={idx} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-1290">
                        <td className="p-4" data-id="element-1291">{comm.date}</td>
                        <td className="p-4" data-id="element-1292">{comm.product_name}</td>
                        <td className="p-4 text-right" data-id="element-1293">{comm.quantity}</td>
                        <td className="p-4 text-right" data-id="element-1294">
                          {formatCurrency(comm.total)}
                        </td>
                        <td className="p-4" data-id="element-1295">{comm.seller_name}</td>
                        <td className="p-4 text-right font-semibold text-purple-600 dark:text-purple-400" data-id="element-1296">
                          {formatCurrency(comm.commission_amount)}
                        </td>
                      </tr>)}
                </tbody>
                {filteredProductCommissions.length > 0 && <tfoot className="bg-gray-50 dark:bg-gray-800 font-semibold" data-id="element-1297">
                    <tr data-id="element-1298">
                      <td colSpan={5} className="p-4 text-right" data-id="element-1299">
                        Total:
                      </td>
                      <td className="p-4 text-right text-purple-600 dark:text-purple-400" data-id="element-1300">
                        {formatCurrency(productTotalCommission)}
                      </td>
                    </tr>
                  </tfoot>}
              </table>
            </div>
          </Card>

          <Pagination {...productPagination} data-id="element-1301" />
        </div>
  }];
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen" data-id="element-1302">
        <div className="text-center" data-id="element-1303">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4" data-id="element-1304"></div>
          <p className="text-gray-600 dark:text-gray-400" data-id="element-1305">
            Cargando comisiones...
          </p>
        </div>
      </div>;
  }
  return <div className="space-y-6 max-w-7xl mx-auto" data-id="element-1306">
      <div className="bg-gradient-to-r from-purple-500 to-indigo-500 p-6 rounded-2xl text-white" data-id="element-1307">
        <div className="flex items-center gap-3" data-id="element-1308">
          <DollarSignIcon className="w-8 h-8" data-id="element-1309" />
          <div data-id="element-1310">
            <h1 className="text-2xl font-bold" data-id="element-1311">Comisiones</h1>
            <p className="text-sm opacity-90" data-id="element-1312">
              Gestión y cálculo de comisiones por servicios y productos
            </p>
          </div>
        </div>
      </div>

      <Tabs tabs={tabs} data-id="element-1313" />

      {/* Edit/Add Modal */}
      {isModalOpen && editingConfig && <Modal isOpen={isModalOpen} onClose={() => {
      setIsModalOpen(false);
      setEditingConfig(null);
    }} title={editingConfig.id ? 'Editar Configuración' : 'Nueva Configuración'} icon={<SettingsIcon className="w-5 h-5" data-id="element-1315" />} accentColor="purple" data-id="element-1314">
          <FormSection title="Información de Comisión" data-id="element-1316">
            <div className="space-y-4" data-id="element-1317">
              <Input label="Nombre de Persona" value={editingConfig.person_name} onChange={e => setEditingConfig({
            ...editingConfig,
            person_name: e.target.value
          })} placeholder="Nombre completo" data-id="element-1318" />

              <Select label="Tipo" options={[{
            value: 'usuario',
            label: 'Usuario'
          }, {
            value: 'doctor',
            label: 'Doctor'
          }]} value={editingConfig.person_type} onChange={value => setEditingConfig({
            ...editingConfig,
            person_type: value as 'usuario' | 'doctor'
          })} data-id="element-1319" />

              <Input label="Porcentaje de Comisión" type="number" min="0" max="100" step="0.01" value={editingConfig.commission_percent} onChange={e => setEditingConfig({
            ...editingConfig,
            commission_percent: parseFloat(e.target.value) || 0
          })} data-id="element-1320" />

              <div className="flex items-center gap-2" data-id="element-1321">
                <input type="checkbox" id="is_active" checked={editingConfig.is_active} onChange={e => setEditingConfig({
              ...editingConfig,
              is_active: e.target.checked
            })} className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500" data-id="element-1322" />
                <label htmlFor="is_active" className="text-sm font-medium" data-id="element-1323">
                  Activo
                </label>
              </div>
            </div>
          </FormSection>

          <div className="flex gap-3 mt-6" data-id="element-1324">
            <Button variant="outline" onClick={() => {
          setIsModalOpen(false);
          setEditingConfig(null);
        }} className="flex-1" data-id="element-1325">
              Cancelar
            </Button>
            <Button onClick={handleSaveConfig} className="flex-1" data-id="element-1326">
              Guardar
            </Button>
          </div>
        </Modal>}
    </div>;
}